import { FileText, Clock } from '@phosphor-icons/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, canUseApp } from '@/auth/AuthContext';
import { useT } from '@/i18n/LanguageContext';

function SegItem({ Icon, label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`relative inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-medium transition-all duration-200 ${
        active
          ? 'bg-white text-zinc-900 shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]'
          : 'text-zinc-600 hover:text-zinc-900'
      }`}
    >
      <Icon size={15} weight={active ? 'fill' : 'regular'} className={active ? 'text-teal-700' : ''} />
      <span>{label}</span>
    </button>
  );
}

/**
 * Segmented control with 申請フォーム / 申請履歴 — the kintai app's sections.
 * Sits left-aligned next to the app chip and only renders on kintai pages
 * (hidden on /it, /chat, /settings, where those apps own their navigation).
 * Chat is reached via the global floating button; 設定 lives in AccountMenu.
 */
export default function DesktopNav({ unverifiedCount = 0 }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useT();
  const isActive = (path) => location.pathname.startsWith(path);

  const p = location.pathname;
  if (p.startsWith('/settings') || p.startsWith('/it') || p.startsWith('/my-it') || p.startsWith('/mfg') || p.startsWith('/shizai') || p.startsWith('/parts-request') || p.startsWith('/marketing') || p.startsWith('/planner') || p.startsWith('/engineering') || p.startsWith('/bihin') || p.startsWith('/chat') || p.startsWith('/knowledge') || p.startsWith('/scanner') || p.startsWith('/documents') || p.startsWith('/demo')) return null;

  const showKintai = canUseApp(user, 'kintai');
  if (!showKintai) return null;

  return (
    <nav className="hidden md:flex items-center gap-2 pl-1" data-testid="nav-center">
      <div className="inline-flex items-center bg-zinc-100/80 rounded-full p-1 gap-0.5">
        <SegItem
          Icon={FileText}
          label={t('nav.forms')}
          active={isActive('/forms')}
          onClick={() => navigate('/forms')}
          testid="nav-dashboard"
        />
        <SegItem
          Icon={Clock}
          label={t('nav.history')}
          active={isActive('/history') || location.pathname.startsWith('/submission')}
          onClick={() => navigate('/history')}
          testid="nav-history"
        />
      </div>
      {unverifiedCount > 0 && (
        <button
          type="button"
          onClick={() => navigate('/history?status=unverified')}
          data-testid="nav-unverified-badge"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 text-red-600 text-[12px] font-semibold border border-red-200 hover:bg-red-100 transition-colors"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          {t('nav.unverified', { n: unverifiedCount })}
        </button>
      )}
    </nav>
  );
}
